'use client';
import { cn } from '@ahm/common-helpers';
import { ArrowDownIcon } from 'lucide-react';
import { useCallback } from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { Button } from '../button';

interface AIConversationProps {
  className?: string;
  children?: React.ReactNode;
  enableAutoScroll?: boolean;
}

export const AIConversation = ({
  className,
  enableAutoScroll = true,
  children,
}: AIConversationProps) => {
  if (!enableAutoScroll) {
    // When auto scroll is disabled, use a regular div instead of StickToBottom
    return (
      <div
        className={cn('relative flex-1 overflow-y-auto', className)}
        role="log"
      >
        {children}
      </div>
    );
  }

  return (
    // @ts-expect-error stick-to-bottom component typing
    <StickToBottom
      className={cn('relative flex-1 overflow-y-auto', className)}
      initial="smooth"
      resize="smooth"
      role="log"
    >
      {/* @ts-expect-error children type */}
      {children}
    </StickToBottom>
  );
};

interface AIConversationContentProps {
  className?: string;
  children?: React.ReactNode;
  enableAutoScroll?: boolean;
}

export const AIConversationContent = ({
  className,
  children,
  enableAutoScroll = true,
}: AIConversationContentProps) => {
  if (!enableAutoScroll) {
    // When auto scroll is disabled, use a regular div
    return <div className={cn('p-4', className)}>{children}</div>;
  }

  return (
    // @ts-expect-error stick-to-bottom component typing
    <StickToBottom.Content className={cn('p-4', className)}>
      {/* @ts-expect-error children type */}
      {children}
    </StickToBottom.Content>
  );
};

export const AIConversationScrollButton = ({
  className,
  enableAutoScroll = true,
}: {
  className?: string;
  enableAutoScroll?: boolean;
}) => {
  // Don't render scroll button if auto scroll is disabled
  if (!enableAutoScroll) {
    return null;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleScrollToBottom = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    void scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn(
          'absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full',
          className
        )}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    )
  );
};
